import { Moment } from 'moment';
import {
  HD_ZODIAC_ASTRONOMICON_GLYPHS,
  hdZodiacPositionFromLongitude,
} from '../chart/bodygraph/hd-gate-coordinates';
import {
  formatHdAbsoluteDegrees,
  formatHdZodiacDegrees,
  hdAbsoluteDegreesFormat,
} from '../chart/bodygraph/gate-info/variants/hd-zodiac-position.util';
import { EphemerisBodyKey, EphemerisDailyTable, EphemerisPosition } from './ephemeris.models';

/** Notatie van de dagposities: decimale graden (rekenen) of zodiac (zoals gedrukte ephemerides). */
export type EphemerisNotation = 'degrees' | 'zodiac';

/** Kolom van de dagtabel: de Maan staat er twee keer (op het rijmoment en `moonOffsetHours` later). */
export interface EphemerisDailyColumn {
  readonly key: EphemerisBodyKey;
  /** true voor de tweede Maankolom (+6 u). */
  readonly moonLater: boolean;
}

export interface EphemerisDailyCellVm {
  /** `267,0806°` of `27°04'50"`. */
  readonly text: string;
  /** Astronomicon-glyph van het teken (enkel zodiac-notatie). */
  readonly signGlyph: string | null;
  readonly retrograde: boolean;
  /** Lengte in graden (voor de tooltip met de andere notaties). */
  readonly longitude: number;
}

export interface EphemerisDailyRowVm {
  readonly id: number;
  readonly atUtc: Moment;
  readonly cells: readonly EphemerisDailyCellVm[];
}

export interface EphemerisDailyRowsVm {
  readonly columns: readonly EphemerisDailyColumn[];
  readonly rows: readonly EphemerisDailyRowVm[];
}

/**
 * Bouwt de cellen van de dagposities (00:00 en 12:00, de Maan ook 6 uur later) in de gekozen
 * notatie. Verborgen lichamen vallen weg; de Maan levert twee kolommen. Tijdzone, datum- en
 * maandkoppen en de rij die nu geldt komen erbij in {@link buildEphemerisDisplay}.
 */
export function buildEphemerisDailyRows(
  daily: EphemerisDailyTable,
  hidden: ReadonlySet<EphemerisBodyKey>,
  notation: EphemerisNotation,
  lang: string,
): EphemerisDailyRowsVm {
  const columns: { column: EphemerisDailyColumn; index: number }[] = [];
  daily.bodies.forEach((key, index) => {
    if (hidden.has(key)) return;
    columns.push({ column: { key, moonLater: false }, index });
    if (key === 'moon') {
      columns.push({ column: { key, moonLater: true }, index });
    }
  });

  const numberFormat = hdAbsoluteDegreesFormat(lang);
  const rows = daily.days.map(
    (day): EphemerisDailyRowVm => ({
      id: day.atUtc.valueOf(),
      atUtc: day.atUtc,
      cells: columns.map(({ column, index }) =>
        toEphemerisDailyCell(column.moonLater ? day.moonLater : day.positions[index], notation, numberFormat),
      ),
    }),
  );

  return { columns: columns.map(entry => entry.column), rows };
}

/** Eén positiecel in de gekozen notatie (ook gebruikt voor de ingevoegde rijen). */
export function toEphemerisDailyCell(
  position: EphemerisPosition,
  notation: EphemerisNotation,
  numberFormat: Intl.NumberFormat,
): EphemerisDailyCellVm {
  if (notation === 'degrees') {
    return {
      text: formatHdAbsoluteDegrees(position.longitude, numberFormat),
      signGlyph: null,
      retrograde: position.retrograde,
      longitude: position.longitude,
    };
  }

  const zodiac = hdZodiacPositionFromLongitude(position.longitude);
  return {
    text: formatHdZodiacDegrees(zodiac),
    signGlyph: HD_ZODIAC_ASTRONOMICON_GLYPHS[zodiac.zodiacSign],
    retrograde: position.retrograde,
    longitude: position.longitude,
  };
}
